import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Upload, FileBox, X, Loader2, CheckCircle2, Clock, Package, Box, Settings2 } from 'lucide-react';
import axios from 'axios';
import { useLang } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { toast } from '../hooks/use-toast';
import {
  TECHNOLOGIES, MATERIALS, LAYER_HEIGHTS, INFILL_OPTIONS,
  estimateVolumeFromFileSize, calculateQuote
} from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Quote = () => {
  const { t } = useLang();
  const fileRef = useRef(null);

  const [file, setFile] = useState(null);
  const [volume, setVolume] = useState(null);
  const [tech, setTech] = useState('sla');
  const [materialId, setMaterialId] = useState('standard');
  const [color, setColor] = useState('Gri');
  const [layer, setLayer] = useState(0.05);
  const [infill, setInfill] = useState(20);
  const [finishId, setFinishId] = useState('none');
  const [quantity, setQuantity] = useState(1);

  const [contact, setContact] = useState({ name: '', email: '', phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const firstMat = MATERIALS[tech][0];
    setMaterialId(firstMat.id);
    setColor(firstMat.colors[0]);
    if (tech === 'fdm') setLayer(0.20);
    else if (tech === 'sla') setLayer(0.05);
    else if (tech === 'sls') setLayer(0.12);
  }, [tech]);

  useEffect(() => {
    const m = MATERIALS[tech].find((x) => x.id === materialId);
    if (m && !m.colors.includes(color)) setColor(m.colors[0]);
  }, [materialId, tech]); // eslint-disable-line

  const currentMaterial = MATERIALS[tech].find((m) => m.id === materialId);
  const currentLayers = LAYER_HEIGHTS[tech];

  const handleFile = (f) => {
    if (!f) return;
    const allowed = ['stl', 'obj', 'step', 'stp', '3mf'];
    const ext = f.name.split('.').pop()?.toLowerCase();
    if (!allowed.includes(ext)) {
      toast({ title: 'Format', description: 'STL, OBJ, STEP veya 3MF yükleyin.' });
      return;
    }
    if (f.size > 100 * 1024 * 1024) {
      toast({ title: 'Boyut', description: 'Maks 100MB.' });
      return;
    }
    setFile(f);
    setVolume(estimateVolumeFromFileSize(f.size));
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    handleFile(f);
  };

  const quote = useMemo(() => {
    if (!file || !volume) return null;
    return calculateQuote({
      technology: tech,
      materialId,
      layerHeight: layer,
      infill,
      finishId,
      quantity,
      volumeCm3: volume
    });
  }, [file, volume, tech, materialId, layer, infill, finishId, quantity]);

  const placeOrder = async () => {
    if (!file) return;
    if (!contact.name || !contact.email) {
      toast({ title: 'Bilgi', description: 'Lütfen ad ve e-posta giriniz.' });
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('payload', JSON.stringify({
        technology: tech,
        material_id: materialId,
        material_label: currentMaterial?.label,
        color,
        layer_height: layer,
        infill,
        finish_id: finishId,
        quantity,
        volume_cm3: volume,
        unit_price: 0,
        total_price: 0,
        lead_min: quote?.leadMin ?? 2,
        lead_max: quote?.leadMax ?? 5,
        contact_name: contact.name,
        contact_email: contact.email,
        contact_phone: contact.phone,
        notes: contact.notes,
        request_type: 'manual_quote'
      }));
      const res = await axios.post(`${API}/orders`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast({ title: 'OK', description: `${t.quote.orderSuccess} ${res.data.order_number}` });
      setFile(null); setVolume(null); setQuantity(1); setContact({ name: '', email: '', phone: '', notes: '' });
    } catch (err) {
      console.error(err);
      toast({ title: 'Hata', description: t.quote.orderError });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "bg-white border-zinc-300 text-zinc-900 focus-visible:ring-orange-500 focus-visible:ring-offset-0";
  const triggerCls = "bg-white border-zinc-300 text-zinc-900";
  const selectContentCls = "bg-white border-zinc-200 text-zinc-900";

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-zinc-900">{t.quote.title}</h1>
          <p className="text-zinc-600 mt-2">{t.quote.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Upload */}
            <Card className="bg-white border-zinc-200 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-7 h-7 rounded-md bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                  <Upload className="w-4 h-4 text-orange-500" />
                </span>
                <h2 className="text-zinc-900 font-semibold">{t.quote.uploadTitle}</h2>
              </div>

              {!file ? (
                <div
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onDrop}
                  className="border-2 border-dashed border-zinc-300 hover:border-orange-400 hover:bg-orange-50 transition-colors rounded-xl p-10 text-center cursor-pointer"
                >
                  <FileBox className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
                  <div className="text-zinc-900 font-medium">{t.quote.dropHere}</div>
                  <div className="text-sm text-zinc-500 mt-1">{t.quote.orBrowse}</div>
                  <div className="text-xs text-zinc-400 mt-3">{t.quote.uploadHint}</div>
                  <input ref={fileRef} type="file" accept=".stl,.obj,.step,.stp,.3mf" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
                </div>
              ) : (
                <div className="flex items-center justify-between border border-zinc-200 bg-zinc-50 rounded-xl p-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center justify-center shrink-0">
                      <Box className="w-5 h-5 text-orange-500" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-zinc-900 font-medium truncate">{file.name}</div>
                      <div className="text-xs text-zinc-500">{(file.size / 1024 / 1024).toFixed(2)} MB · ~{volume} cm³</div>
                    </div>
                  </div>
                  <button onClick={() => { setFile(null); setVolume(null); }} className="text-zinc-500 hover:text-orange-500 p-2" aria-label="remove">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </Card>

            {/* Parameters */}
            <Card className="bg-white border-zinc-200 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5">
                <span className="w-7 h-7 rounded-md bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                  <Settings2 className="w-4 h-4 text-orange-500" />
                </span>
                <h2 className="text-zinc-900 font-semibold">{t.quote.configTitle}</h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-zinc-700 mb-2 block">{t.quote.technology}</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {TECHNOLOGIES.map((x) => (
                      <button
                        key={x.id}
                        onClick={() => setTech(x.id)}
                        className={`py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
                          tech === x.id ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-zinc-700 border-zinc-300 hover:border-orange-400 hover:text-orange-600'
                        }`}
                      >
                        {x.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-zinc-700 mb-2 block">{t.quote.material}</Label>
                  <Select value={materialId} onValueChange={setMaterialId}>
                    <SelectTrigger className={triggerCls}><SelectValue /></SelectTrigger>
                    <SelectContent className={selectContentCls}>
                      {MATERIALS[tech].map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-zinc-700 mb-2 block">{t.quote.color}</Label>
                  <Select value={color} onValueChange={setColor}>
                    <SelectTrigger className={triggerCls}><SelectValue /></SelectTrigger>
                    <SelectContent className={selectContentCls}>
                      {currentMaterial?.colors.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-zinc-700 mb-2 block">{t.quote.quality}</Label>
                  <Select value={String(layer)} onValueChange={(v) => setLayer(parseFloat(v))}>
                    <SelectTrigger className={triggerCls}><SelectValue /></SelectTrigger>
                    <SelectContent className={selectContentCls}>
                      {currentLayers.map((l) => (
                        <SelectItem key={l.id} value={String(l.id)}>{l.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {tech === 'fdm' && (
                  <div className="sm:col-span-2">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-zinc-700">{t.quote.infill}</Label>
                      <span className="text-orange-600 font-semibold text-sm">{infill}%</span>
                    </div>
                    <Slider
                      value={[infill]}
                      min={10}
                      max={100}
                      step={5}
                      onValueChange={(v) => setInfill(v[0])}
                      className="[&_[role=slider]]:bg-orange-500 [&_[role=slider]]:border-orange-500"
                    />
                    <div className="flex justify-between text-xs text-zinc-500 mt-2">
                      {INFILL_OPTIONS.map((p) => <span key={p}>{p}%</span>)}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-zinc-700 mb-2 block">{t.quote.quantity}</Label>
                  <Input
                    type="number"
                    min={1}
                    max={1000}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className={inputCls}
                  />
                </div>
              </div>
            </Card>

            {/* Contact */}
            <Card className="bg-white border-zinc-200 p-6 shadow-sm">
              <h2 className="text-zinc-900 font-semibold mb-5">{t.quote.contactInfo}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-700 mb-2 block">{t.quote.fullName} *</Label>
                  <Input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <Label className="text-zinc-700 mb-2 block">{t.quote.email} *</Label>
                  <Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-zinc-700 mb-2 block">{t.quote.phone}</Label>
                  <Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-zinc-700 mb-2 block">{t.quote.notes}</Label>
                  <Textarea rows={3} value={contact.notes} onChange={(e) => setContact({ ...contact, notes: e.target.value })} className={inputCls} />
                </div>
              </div>
            </Card>
          </div>

          {/* RIGHT: Summary */}
          <div className="lg:col-span-1">
            <Card className="bg-white border-orange-200 p-6 sticky top-28 shadow-md">
              <h2 className="text-zinc-900 font-semibold flex items-center gap-2">
                <Package className="w-4 h-4 text-orange-500" /> {t.quote.summaryTitle}
              </h2>

              {!file ? (
                <div className="mt-6 text-center py-8">
                  <FileBox className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                  <p className="text-zinc-500 text-sm">{t.quote.noFile}</p>
                </div>
              ) : (
                <>
                  <div className="mt-5 space-y-3 text-sm">
                    <Row label={t.quote.technology} value={tech.toUpperCase()} />
                    <Row label={t.quote.material} value={currentMaterial?.label} />
                    <Row label={t.quote.color} value={color} />
                    <Row label={t.quote.quality} value={`${layer} mm`} />
                    {tech === 'fdm' && <Row label={t.quote.infill} value={`${infill}%`} />}
                    <Row label={t.quote.quantity} value={quantity} />
                  </div>

                  <div className="my-5 h-px bg-zinc-200" />

                  {quote && (
                    <div className="flex items-center gap-2 text-sm text-zinc-600 mb-4">
                      <Clock className="w-4 h-4 text-orange-500" />
                      <span>{t.quote.leadTime}: <span className="text-zinc-900 font-medium">{quote.leadMin}-{quote.leadMax} {t.quote.days}</span></span>
                    </div>
                  )}

                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs text-zinc-700 leading-relaxed">
                    {t.quote.manualNote}
                  </div>

                  <Button
                    onClick={placeOrder}
                    disabled={submitting}
                    className="w-full mt-5 bg-orange-500 hover:bg-orange-600 text-white font-semibold border-0 h-12"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    {t.quote.placeOrder}
                  </Button>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-zinc-500">{label}</span>
    <span className="text-zinc-800 font-medium">{value}</span>
  </div>
);

export default Quote;
